// src/App.tsx

import { useState, useEffect } from 'react';
import Dashboard from './components/Dashboard';
import TestConsole from './components/TestConsole';
import HealthCheck from './components/HealthCheck';
import Settings from './components/Settings';
import type { Stats } from './types';
import apiService from './services/api'; // Now using real API
import './App.css';

type TabType = 'dashboard' | 'test' | 'health' | 'settings';

function App() {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [health, setHealth] = useState<'healthy' | 'warning' | 'unhealthy'>('healthy');
  const [stats, setStats] = useState<Stats>({
    totalRequests: 0,
    cacheHitRate: 0,
    avgResponseTime: 0,
    activeConnections: 0,
    errorsToday: 0,
    torNodesCount: 0,
  });
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isConnected, setIsConnected] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const newStats = await apiService.getStats();
      setStats(newStats);
      setLastUpdated(new Date());
      setIsConnected(true);
      setError('');
      
      // Update health status based on errors
      if (newStats.errorsToday > 10) {
        setHealth('unhealthy');
      } else if (newStats.errorsToday > 5) {
        setHealth('warning');
      } else {
        setHealth('healthy');
      }
    } catch (err: any) {
      console.error('Error loading stats:', err);
      setIsConnected(false);
      setHealth('unhealthy');
      setError('Cannot connect to IpInfo API. Make sure it is running.');
    }
  };

  return (
    <div className="app">
      <header className="header">
        <h1>
          IpInfo Admin
          <span className={`status-indicator ${health}`}></span>
        </h1>
        <div className="header-info">
          {isConnected ? (
            <span>Last Updated: {lastUpdated.toLocaleTimeString()}</span>
          ) : (
            <span className="error-text">⚠ Disconnected - {error}</span>
          )}
        </div>
      </header>

      <nav className="nav-tabs">
        <button
          className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard
        </button>
        <button
          className={`nav-tab ${activeTab === 'test' ? 'active' : ''}`}
          onClick={() => setActiveTab('test')}
        >
          Test Console
        </button>
        <button
          className={`nav-tab ${activeTab === 'health' ? 'active' : ''}`}
          onClick={() => setActiveTab('health')}
        >
          Health Check
        </button>
        <button
          className={`nav-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
      </nav>

      <main className="content">
        {!isConnected && (
          <div className="error-banner">
            <p>{error}</p>
            <button onClick={loadStats}>Retry Connection</button>
          </div>
        )}
        {activeTab === 'dashboard' && <Dashboard stats={stats} />}
        {activeTab === 'test' && <TestConsole />}
        {activeTab === 'health' && <HealthCheck />}
        {activeTab === 'settings' && <Settings />}
      </main>
    </div>
  );
}

export default App;
